import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Route, Routes } from 'react-router-dom';
import { redeemEntryCode } from '../../apiservice/entryCode';
import { initializeSessionFromRedirect } from '../../apiservice/interceptor';
import { setItem } from '../../auth/localStorageManager';
import { sessionManager } from '../../auth/sessionManager';
import {
  diagnosisHistoryLink,
  errorLink,
  remoteDiagnosisLink,
} from '../../common/links';
import Loader from '../../components/common/Loader';
import { useLoader } from '../../context/LoaderContext';
import { RedirectParameterContext } from '../../context/RedirectParameterContext';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import useQueryParams from '../../hooks/useQueryParams';
import { handleBeforeUnload } from '../../utils/utilFunctions';
import RootPage from './RootPage';

// Lazy load page components for code splitting
const DiagnosisHistory = lazy(() => import('../DiagnosisHistory'));
const ErrorPage = lazy(() => import('../Error'));
const RemoteDiagnosis = lazy(
  () => import('../RemoteDiagnosis/RemoteDiagnosis')
);

// The support screen reads the device and service context from the URL (the
// remote-diagnosis page opens the device only when `uuid` is present). A v1 entry
// URL carried these directly; a v3 URL carries only the code, so after redemption
// the values are put back under the same v1 query names. `code: null` drops the
// spent code in the same navigation. The token itself stays out of the URL.
const toEntryUrlParams = entry => ({
  code: null,
  uuid: entry.deviceId,
  svcId: entry.svcId,
  svcManagerId: entry.managerId,
  svcAsId: entry.asId,
  svcSymptomCode: entry.symptomCode,
  createDate: entry.createDate,
  createTime: entry.createTime,
});

const BaseLayout = ({ platform }) => {
  const params = new URLSearchParams(window.location.search);
  const { getAllParams, removeParam, setParams } = useQueryParams();
  const urlParams = getAllParams();
  const { redirectParameters } = useContext(RedirectParameterContext);

  useBeforeUnload(() => handleBeforeUnload(urlParams, redirectParameters));

  const token = params.get('accessToken');
  const entryCode = params.get('code');
  const { isLoading } = useLoader();

  const [isTokenValid, setisTokenValid] = useState(
    () => sessionManager.isSessionValid() && Boolean(sessionManager.getToken())
  );

  // dev dropped the validateSession round trip (9b7932d5, file deleted): a session that
  // arrives through a trusted entry is marked valid locally. Both entry paths share it.
  const openSession = useCallback(() => {
    sessionManager.markSessionValid();
    setisTokenValid(true);
    setItem('user_id', '');
  }, []);

  const init = useCallback(() => {
    initializeSessionFromRedirect(); // set in session storage
    // The partner entry URL carries the access token in the query string, so the
    // browser sends it as the Referer of every later request. The token is now in
    // session storage, so drop it from the address bar right away.
    // Uses removeParam (react-router) rather than history.replaceState so the router
    // location stays in sync - otherwise a later setParam/setParams call rebuilds the
    // query from a stale snapshot and puts the token back.
    removeParam('accessToken');
    openSession();
  }, [removeParam, openSession]);

  // v3 entry: the URL carries a single-use code instead of the token. Exchange it
  // once, then strip it so a spent code does not linger in the address bar or in
  // the Referer of the requests this page is about to make.
  const enterWithCode = useCallback(
    code => {
      let cancelled = false;

      redeemEntryCode(code)
        .then(entry => {
          if (cancelled) return;
          if (!entry) {
            removeParam('code');
            sessionManager.cleanupAndRedirect();
            return;
          }
          setParams(toEntryUrlParams(entry));
          openSession();
        })
        .catch(() => {
          if (cancelled) return;
          removeParam('code');
          sessionManager.cleanupAndRedirect();
        });

      return () => {
        cancelled = true;
      };
    },
    [removeParam, setParams, openSession]
  );

  // Deliberately keyed on the two URL values only. removeParam depends on searchParams,
  // so init/enterWithCode change identity as soon as the parameter is stripped; listing
  // them here would re-run the effect and redeem an already-spent code.
  useEffect(() => {
    if (token) {
      init();
      return undefined;
    }
    if (entryCode) {
      return enterWithCode(entryCode);
    }
    return undefined;
  }, [token, entryCode]);

  return (
    <>
      <Loader isOpen={isLoading} />
      <Routes>
        <Route path="/" element={<RootPage />} />
        {isTokenValid && (
          <>
            <Route
              path={remoteDiagnosisLink}
              element={
                <Suspense>
                  <RemoteDiagnosis platform={platform} />
                </Suspense>
              }
            />
            <Route
              path={diagnosisHistoryLink}
              element={
                <Suspense>
                  <DiagnosisHistory platform={platform} />
                </Suspense>
              }
            />
          </>
        )}
        <Route
          path={errorLink}
          element={
            <Suspense>
              <ErrorPage />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
};

export default BaseLayout;
